import React from 'react';
import { motion, easeOut } from 'framer-motion';
import { Database, AlertTriangle, Settings, RefreshCw } from 'lucide-react';

interface TableNotFoundCardProps {
  message: string;
}

export const TableNotFoundCard: React.FC<TableNotFoundCardProps> = ({ message }) => {
  const containerVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: easeOut
      }
    }
  };

  const shakeVariants = {
    shake: {
      x: [-5, 5, -5, 5, 0],
      transition: {
        duration: 0.5,
        repeat: Infinity,
        repeatDelay: 2
      }
    }
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="max-w-lg mx-auto"
    >
      <div className="bg-gradient-to-br from-red-50 to-pink-50 border border-red-200 rounded-2xl p-8 shadow-2xl text-center">
        <motion.div
          variants={shakeVariants}
          animate="shake"
          className="flex items-center justify-center mb-6"
        >
          <div className="relative">
            <div className="w-20 h-20 bg-gradient-to-br from-red-500 to-pink-600 rounded-full flex items-center justify-center">
              <Database className="w-10 h-10 text-white" />
            </div>
            <motion.div
              initial={{ scale: 0, rotate: 180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ delay: 0.3, type: "spring", stiffness: 300 }}
              className="absolute -top-2 -right-2 w-8 h-8 bg-red-600 rounded-full flex items-center justify-center"
            >
              <AlertTriangle className="w-5 h-5 text-white" />
            </motion.div>
          </div>
        </motion.div>

        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="text-3xl font-bold text-gray-800 mb-4"
        >
          Database Error
        </motion.h2>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="text-gray-600 mb-6 leading-relaxed"
        >
          {message}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 space-y-4"
        >
          <div className="flex items-center justify-center space-x-3 mb-4">
            <Settings className="w-6 h-6 text-red-500" />
            <h3 className="text-lg font-semibold text-gray-800">System Status</h3>
          </div>
          
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
              <span className="text-sm text-gray-700">Database Connection</span>
              <span className="text-sm font-medium text-red-600">Disconnected</span>
            </div>
            
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <span className="text-sm text-gray-700">Table Status</span>
              <span className="text-sm font-medium text-gray-600">Not Found</span>
            </div>
            
            <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
              <span className="text-sm text-gray-700">Service Status</span>
              <span className="text-sm font-medium text-yellow-600">Maintenance</span>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="flex items-center justify-center mt-4 p-3 bg-blue-50 rounded-lg"
          >
            <RefreshCw className="w-4 h-4 text-blue-600 mr-2" />
            <span className="text-sm text-blue-600">System recovery in progress...</span>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
          className="mt-6 text-xs text-gray-500"
        >
          Please contact your system administrator if this issue persists.
        </motion.div>
      </div>
    </motion.div>
  );
};